// Left-side sections sidebar. Slack-style — Personal (computed) +
// named sections (visible to the current user's roles) + a "+ section"
// affordance for owners/admins. Selecting a section filters the canvas.
// Each row has an owner/admin-only kebab → "Edit access" (Option A UX).

import { useEffect, useState } from "react"
import { useStore } from "../../stores/store"
import {
  sectionsStore, PERSONAL_SECTION_ID,
  loadSections, createSection, selectSection,
  openSectionMenu, closeSectionMenu, openSectionEditor,
} from "../../stores/sectionsStore"
import { useBootstrap, useTranslations } from "../../lib/translations"
import { SectionEditor } from "./SectionEditor"

export function SectionsSidebar() {
  const t = useTranslations()
  const bootstrap = useBootstrap()
  const sections = useStore(sectionsStore, (s) => s.sections)
  const loaded = useStore(sectionsStore, (s) => s.loaded)
  const currentSectionId = useStore(sectionsStore, (s) => s.currentSectionId)
  const saving = useStore(sectionsStore, (s) => s.saving)
  const editingSectionId = useStore(sectionsStore, (s) => s.editingSectionId)

  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState("")

  const canManage = (bootstrap.memberships || []).some((m) =>
    Array.isArray(m.roles) && (m.roles.includes("owner") || m.roles.includes("admin")) &&
    m.company_id === bootstrap.current_company?.id
  )

  useEffect(() => {
    loadSections(bootstrap.paths)
  }, [bootstrap.paths])

  // Click anywhere outside a kebab → close any open menu.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest(".cm-section-kebab-wrap")) closeSectionMenu()
    }
    window.addEventListener("click", onDocClick)
    return () => window.removeEventListener("click", onDocClick)
  }, [])

  const submit = async (e) => {
    e?.preventDefault?.()
    if (!newName.trim() || saving) return
    try {
      await createSection(bootstrap.paths, { name: newName.trim() })
      setNewName("")
      setCreating(false)
    } catch (err) {
      alert(err.message)
    }
  }

  return (
    <aside className="cm-sidebar">
      <div className="cm-sidebar-title">{t("sidebar.sections")}</div>

      <ul className="cm-section-list">
        <SectionRow
          icon="lock"
          name={t("sidebar.personal")}
          active={currentSectionId === PERSONAL_SECTION_ID}
          onClick={() => selectSection(PERSONAL_SECTION_ID)}
        />
        {sections.map((s) => (
          <SectionRow
            key={s.id}
            section={s}
            icon="hash"
            color={s.color}
            name={s.name}
            active={currentSectionId === s.id}
            canManage={canManage}
            onClick={() => selectSection(s.id)}
          />
        ))}
        {loaded && sections.length === 0 && (
          <li className="cm-section-empty">{t("sidebar.empty")}</li>
        )}
      </ul>

      {canManage && !creating && (
        <button type="button" className="cm-section-add" onClick={() => setCreating(true)}>
          <svg viewBox="0 0 24 24"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5z" /></svg>
          <span>{t("sidebar.add")}</span>
        </button>
      )}

      {creating && (
        <form className="cm-section-create" onSubmit={submit}>
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder={t("sidebar.add_placeholder")} disabled={saving} />
          <div className="cm-section-create-actions">
            <button type="submit" disabled={saving || !newName.trim()}>{t("sidebar.add_submit")}</button>
            <button type="button" onClick={() => { setCreating(false); setNewName("") }}>
              {t("sidebar.add_cancel")}
            </button>
          </div>
        </form>
      )}

      {editingSectionId != null && <SectionEditor sectionId={editingSectionId} />}
    </aside>
  )
}

function SectionRow({ section, icon, color, name, active, canManage, onClick }) {
  const t = useTranslations()
  const menuOpenForId = useStore(sectionsStore, (s) => s.menuSectionId)
  const showKebab = canManage && section?.id != null
  const menuOpen = showKebab && menuOpenForId === section.id

  const onKebab = (e) => {
    e.stopPropagation()
    if (menuOpen) closeSectionMenu()
    else openSectionMenu(section.id)
  }

  const onEditAccess = (e) => {
    e.stopPropagation()
    openSectionEditor(section.id)
  }

  return (
    <li className={`cm-section-row ${active ? "on" : ""}`} onClick={onClick}>
      <span className="cm-section-icon" style={color ? { background: color } : undefined}>
        {icon === "lock" ? (
          <svg viewBox="0 0 24 24"><path d="M12 2a5 5 0 00-5 5v3H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2v-8a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm0 2a3 3 0 013 3v3H9V7a3 3 0 013-3z" /></svg>
        ) : (
          <svg viewBox="0 0 24 24"><path d="M10 3h2l-1 4h3l1-4h2l-1 4h3v2h-3.4l-.6 4H18v2h-3.6l-1 4h-2l1-4H9.4l-1 4h-2l1-4H4v-2h3.4l.6-4H4V7h4l1-4zm-.4 6l-.6 4h3l.6-4h-3z" /></svg>
        )}
      </span>
      <span className="cm-section-name">{name}</span>
      {showKebab && (
        <span className="cm-section-kebab-wrap">
          <button type="button" className="cm-section-kebab"
                  onClick={onKebab}
                  aria-label={t("sidebar.menu.label")}>
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
          {menuOpen && (
            <div className="cm-section-menu" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={onEditAccess}>{t("sidebar.menu.edit_access")}</button>
            </div>
          )}
        </span>
      )}
    </li>
  )
}
